import { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  Grid,
  InlineStack,
  Modal,
  ModalBody,
  ModalFooter,
  OptionCard,
  Search,
  SkeletonShape,
  Stack,
  Text,
  Truncated,
  type PlumaManagedModalProps,
} from '@customerio/pluma-components/react';
import { useAttributeNamesFirstPage } from '@/services/db/attribute/queries';
import { formatNumber } from '@/utils/number';
import { MAX_RELATIONSHIP_COLUMNS, useSaveRelationshipColumns } from './use-relationship-columns';

const SEARCH_DEBOUNCE_MS = 300;
const SKELETON_CARDS = Array.from({ length: 25 }, (_, index) => index);

export type RelationshipColumnsModalData = {
  workspaceId: number;
  objectTypeId: string;
  selected: readonly string[];
};

type AttributeCardProps = {
  attribute: string;
  isSelected: boolean;
  canSelect: boolean;
  isPlaceholder: boolean;
  onCheckedChange: (checked: boolean) => void;
};

/** One attribute as a checkbox card; a placeholder marks a free slot in the selection. */
function AttributeCard({ attribute, isSelected, canSelect, isPlaceholder, onCheckedChange }: AttributeCardProps) {
  return (
    <OptionCard
      value={attribute}
      inputType="checkbox"
      size="sm"
      isSelected={isSelected}
      canSelect={canSelect}
      isDisabled={isPlaceholder}
      ariaLabel={isPlaceholder ? 'Free attribute slot' : attribute}
      onCheckedChange={onCheckedChange}
      data-test-attribute-card={attribute}
    >
      <Flex justifyContent="center" w="full" minW="0">
        <Truncated color={isSelected || canSelect ? undefined : 'disabled'} textTransform="none">
          {isPlaceholder ? '…' : attribute}
        </Truncated>
      </Flex>
    </OptionCard>
  );
}

type SelectedSlotsProps = {
  selected: readonly string[];
  onClear: () => void;
  onRemove: (attribute: string) => void;
};

/** The picked attributes, one card each, with a placeholder per free slot. */
function SelectedSlots({ selected, onClear, onRemove }: SelectedSlotsProps) {
  const slots = [...selected, ...Array.from({ length: MAX_RELATIONSHIP_COLUMNS - selected.length }, () => '')];

  return (
    <Stack gap="150">
      <InlineStack alignBlock="center" alignInline="space-between">
        <Text display="block" color="subtle">
          Choose up to four custom table attributes
        </Text>
        {selected.length > 0 ? (
          <Button size="sm" onClick={onClear} data-test-clear-button>
            Clear all
          </Button>
        ) : null}
      </InlineStack>
      <Grid columns={2} gap="100" data-test-selected-attributes>
        {slots.map((attribute, index) => (
          <AttributeCard
            // A free slot has no name of its own; its position is its identity.
            key={attribute === '' ? `slot-${index}` : attribute}
            attribute={attribute}
            isSelected={attribute !== ''}
            canSelect
            isPlaceholder={attribute === ''}
            onCheckedChange={(checked) => {
              if (!checked) onRemove(attribute);
            }}
          />
        ))}
      </Grid>
    </Stack>
  );
}

type AvailableAttributesProps = {
  names: readonly string[];
  isLoading: boolean;
  search: string;
  inUseOnly: boolean;
  selected: readonly string[];
  onToggle: (attribute: string, checked: boolean) => void;
};

/** The catalog to pick from: a skeleton while it loads, the empty copy, or a card per name. */
function AvailableAttributes({ names, isLoading, search, inUseOnly, selected, onToggle }: AvailableAttributesProps) {
  if (isLoading) {
    return (
      <Grid gap="100" columns={4} data-test-attributes-loading>
        {SKELETON_CARDS.map((index) => (
          <SkeletonShape key={index} overflow="hidden" w="full" minW="0" style={{ height: '24px' }} />
        ))}
      </Grid>
    );
  }

  if (names.length === 0) {
    return (
      <Box>
        <Text color="subtle" my="150">
          {search === ''
            ? 'No attributes available.'
            : `No ${inUseOnly ? 'attributes in use' : 'unused attributes'} match ${search}.`}
        </Text>
      </Box>
    );
  }

  const canAddColumn = selected.length < MAX_RELATIONSHIP_COLUMNS;
  return (
    <Grid columns={4} gap="100" data-test-visible-attributes>
      {names.map((attribute) => (
        <AttributeCard
          key={attribute}
          attribute={attribute}
          isSelected={selected.includes(attribute)}
          canSelect={canAddColumn}
          isPlaceholder={false}
          onCheckedChange={(checked) => onToggle(attribute, checked)}
        />
      ))}
    </Grid>
  );
}

/**
 * Pick up to four relationship attributes for the table's columns, from the
 * type's relationship attribute names (server-searched, first page only —
 * a longer list asks for a narrower search rather than paging).
 */
export function RelationshipColumnsModal({
  data,
  modalState,
  onClose,
  onConfirm,
}: PlumaManagedModalProps<RelationshipColumnsModalData>) {
  const [selected, setSelected] = useState<string[]>([...data.selected]);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [inUseOnly, setInUseOnly] = useState(false);
  const attributes = useAttributeNamesFirstPage(data.workspaceId, {
    objectTypeId: data.objectTypeId,
    relationship: true,
    search: search === '' ? undefined : search,
    inUseOnly,
  });
  const saveColumns = useSaveRelationshipColumns(data.workspaceId, data.objectTypeId);
  const names = attributes.data?.names ?? [];
  const total = attributes.data?.total ?? 0;

  const toggle = (attribute: string, checked: boolean) =>
    setSelected((current) =>
      checked ? [...current, attribute] : current.filter((candidate) => candidate !== attribute),
    );

  const save = async () => {
    try {
      await saveColumns.mutateAsync(selected);
    } catch {
      return;
    }
    onConfirm();
  };

  return (
    <Modal state={modalState} title="Edit displayed attributes" data-test-relationship-columns-modal>
      <ModalBody>
        <InlineStack alignBlock="flex-end" alignInline="space-between">
          <InlineStack gap="100" alignBlock="center" alignInline="space-between">
            <Search
              label="Find attributes"
              value={searchInput}
              onSearchInput={setSearchInput}
              onSearch={setSearch}
              shouldSearchOnClear
              shouldSearchOnDebounce={SEARCH_DEBOUNCE_MS}
              data-test-attributes-search-input
            />
            <Checkbox
              value="in_use_only"
              label="Attributes in use"
              isChecked={inUseOnly}
              isDisabled={attributes.isFetching}
              onCheckedChange={setInUseOnly}
              mt="200"
            />
          </InlineStack>
          <Text mb="025" data-test-attributes-count-text>
            {selected.length} of {MAX_RELATIONSHIP_COLUMNS} selected
          </Text>
        </InlineStack>

        <Divider my="200" />
        <SelectedSlots selected={selected} onClear={() => setSelected([])} onRemove={(name) => toggle(name, false)} />
        <Divider my="200" />

        <Stack mb="150">
          {attributes.isFetching ? null : (
            <Text color="subtle" data-test-visible-attributes-trailer-text>
              Showing {formatNumber(names.length)} of {formatNumber(total)}.
              {names.length === total ? null : ' Refine your search.'}
            </Text>
          )}
          {saveColumns.isError ? (
            <Text color="critical" data-test-save-error>
              Save failed. Try again or contact support for help.
            </Text>
          ) : null}
        </Stack>

        <AvailableAttributes
          names={names}
          isLoading={attributes.isFetching}
          search={search}
          inUseOnly={inUseOnly}
          selected={selected}
          onToggle={toggle}
        />
      </ModalBody>
      <ModalFooter>
        <Button variant="secondary" onClick={() => onClose()} data-test-cancel-button>
          Cancel
        </Button>
        <Button variant="primary" isLoading={saveColumns.isPending} onClick={() => void save()} data-test-save-button>
          Save attributes
        </Button>
      </ModalFooter>
    </Modal>
  );
}
